import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Close, Refresh, Visibility } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import {
  AUTH_NUMBER_PREFIX,
  FORM_REFS,
  RESULT_OPTIONS,
  STATUS_COLORS,
  STATUS_LABELS
} from '../../constants/oneOffAuthorization';
import GetsCm002aApplicationForm, {
  emptyGetsCm002aForm
} from './GetsCm002aApplicationForm';
import GetsCm001aCertificateForm, {
  emptyGetsCm001aCertificate
} from './GetsCm001aCertificateForm';
import GetsBrandHeader from '../certificationAuthorization/GetsBrandHeader';
import { downloadAuthenticatedPdf } from '../../utils/authenticatedPdf';
import {
  deleteApplicationAttachment,
  downloadApplicationAttachment,
  uploadApplicationAttachments,
  viewApplicationAttachment
} from '../../utils/authorizationAttachments';

const API = '/api/one-off-authorization';

const emptyForm = (user) =>
  emptyGetsCm002aForm({
    requestOriginator: {
      name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
      designation: '',
      date: dayjs().format('YYYY-MM-DD')
    },
    requestDate: dayjs().format('YYYY-MM-DD')
  });

/**
 * @param {object} props
 * @param {boolean} [props.embedded]
 * @param {'full'|'crew'|'mro'} [props.variant]
 */
function OneOffAuthorizationPage({ embedded = false, variant = 'full' } = {}) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [docTab, setDocTab] = useState(0);
  const [form, setForm] = useState(() => emptyForm());
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        requestOriginator: {
          ...(prev.requestOriginator || {}),
          name:
            prev.requestOriginator?.name ||
            `${user.firstName || ''} ${user.lastName || ''}`.trim()
        }
      }));
    }
  }, [user]);

  const [managerDecision, setManagerDecision] = useState({
    decision: 'ACCEPTED',
    remarks: ''
  });

  const [mroDecision, setMroDecision] = useState({
    decision: 'ACCEPTED',
    remarks: '',
    checkedLicences: true,
    checkedTrainings: true,
    checkedExperience: true,
    checkedCompanyApproval: true,
    overallResult: 'SATISFACTORY'
  });

  const perms = meta?.permissions || {};
  const canApply = perms.canApply;
  const canMroManager = Boolean(perms.canMroManagerReview);
  const canMroQm = perms.canMroQmReview || perms.canMroQmManage;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [metaRes, listRes] = await Promise.all([
        axios.get(`${API}/meta`),
        axios.get(API)
      ]);
      setMeta(metaRes.data);
      setRows(listRes.data || []);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load one-off authorizations';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/${id}`);
      setDocTab(0);
      setDetail(res.data);
      if (res.data?.mroQmCheck) {
        setMroDecision((d) => ({
          ...d,
          decision: res.data.mroQmCheck.decision || d.decision,
          remarks: res.data.mroQmCheck.remarks || '',
          checkedLicences: res.data.mroQmCheck.checkedLicences ?? d.checkedLicences,
          checkedTrainings: res.data.mroQmCheck.checkedTrainings ?? d.checkedTrainings,
          checkedExperience: res.data.mroQmCheck.checkedExperience ?? d.checkedExperience,
          checkedCompanyApproval:
            res.data.mroQmCheck.checkedCompanyApproval ?? d.checkedCompanyApproval,
          overallResult: res.data.mroQmCheck.overallResult || d.overallResult
        }));
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load application', 'error');
    }
  };

  const handleCreate = async () => {
    if (!form.personName?.trim() && !form.reasonForOneOff?.trim()) {
      showToast('Person name or reason for one-off authorization is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.applicationAttachments;
      const res = await axios.post(API, payload);
      let created = res.data;
      if (pendingFiles.length) {
        try {
          const withAttachments = await uploadApplicationAttachments(API, created._id, pendingFiles);
          if (withAttachments) created = withAttachments;
        } catch (uploadErr) {
          showToast(
            uploadErr.response?.data?.message || 'Draft created, but some attachments failed to upload',
            'warning'
          );
        }
      }
      showToast(`Draft application created (${FORM_REFS.application})`, 'success');
      setCreateOpen(false);
      setForm(emptyForm(user));
      setPendingFiles([]);
      await load();
      setDetail(created);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create application', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPendingFiles = (files) => {
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadApplicationAttachment = async (files) => {
    if (!detail?._id || !files?.length) return;
    setSaving(true);
    try {
      const updated = await uploadApplicationAttachments(API, detail._id, files);
      if (updated) setDetail(updated);
      showToast(files.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApplicationAttachment = async (att) => {
    if (!detail?._id || !att?._id) return;
    setSaving(true);
    try {
      const updated = await deleteApplicationAttachment(API, detail._id, att._id);
      setDetail(updated);
      showToast('Attachment removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove attachment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/${detail._id}`, {
        reasonForOneOff: detail.reasonForOneOff,
        aircraftType: detail.aircraftType,
        aircraftReg: detail.aircraftReg,
        msn: detail.msn,
        location: detail.location,
        requestDate: detail.requestDate,
        operatorName: detail.operatorName,
        personName: detail.personName,
        countryOfIssue: detail.countryOfIssue,
        ameLicenseNo: detail.ameLicenseNo,
        licenseValidTill: detail.licenseValidTill,
        licenseRatings: detail.licenseRatings,
        licenseCategory: detail.licenseCategory,
        maintenanceOrgNameApproval: detail.maintenanceOrgNameApproval,
        briefMaintenanceTask: detail.briefMaintenanceTask,
        remarks: detail.remarks,
        confirmedMaintenanceData: detail.confirmedMaintenanceData,
        confirmedToolsEquipment: detail.confirmedToolsEquipment,
        requestOriginator: detail.requestOriginator
      });
      setDetail(res.data);
      showToast('Application saved', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/${detail._id}/submit`);
      setDetail(res.data);
      showToast('Submitted to MRO Manager', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Submit failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleManagerDecide = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/${detail._id}/mro-manager/decide`, managerDecision);
      setDetail(res.data);
      showToast(
        managerDecision.decision === 'ACCEPTED'
          ? 'Accepted — forwarded to MRO QM'
          : 'Returned to applicant',
        managerDecision.decision === 'ACCEPTED' ? 'success' : 'warning'
      );
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Decision failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMroDecide = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/${detail._id}/mro-qm/decide`, mroDecision);
      setDetail(res.data);
      showToast(
        mroDecision.decision === 'ACCEPTED'
          ? 'Accepted — proceed to issue authorization card'
          : 'Returned to applicant',
        mroDecision.decision === 'ACCEPTED' ? 'success' : 'warning'
      );
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Decision failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadApplicationPdf = async () => {
    if (!detail?._id) return;
    try {
      await downloadAuthenticatedPdf(
        `${API}/${detail._id}/application/pdf`,
        `GETS-CM-002A_${(detail.personName || 'applicant').replace(/\s+/g, '_')}.pdf`
      );
    } catch (err) {
      showToast(err.message || 'Failed to download application PDF', 'error');
    }
  };

  const buildCertificatePayload = (cert, issued) => ({
    issued: Boolean(issued),
    authorizationNo: cert.authorizationNo,
    issueNo: cert.issueNo,
    revNo: cert.revNo,
    dated: cert.dated || dayjs().format('YYYY-MM-DD'),
    validFrom: cert.validFrom,
    validUntil: cert.validUntil,
    limitations: cert.limitations,
    scopeRows: cert.scopeRows,
    name: cert.name,
    licenseNo: cert.licenseNo,
    category: cert.category,
    ratings: cert.ratings,
    maintenanceOrgName: cert.maintenanceOrgName,
    aircraftModel: cert.aircraftModel,
    aircraftReg: cert.aircraftReg,
    engineModel: cert.engineModel,
    aircraftMsn: cert.aircraftMsn,
    location: cert.location,
    operatorName: cert.operatorName,
    reasonForAuth: cert.reasonForAuth,
    authDate: cert.authDate
  });

  const ensureCertificateDraft = (detailDoc) => {
    const existing = detailDoc?.certificate || {};
    const fromApp = detailDoc || {};
    return emptyGetsCm001aCertificate({
      ...existing,
      authorizationNo: existing.authorizationNo || AUTH_NUMBER_PREFIX,
      issueNo: existing.issueNo || '01',
      revNo: existing.revNo || '00',
      dated: existing.dated
        ? String(existing.dated).slice(0, 10)
        : dayjs().format('YYYY-MM-DD'),
      validFrom: existing.validFrom
        ? String(existing.validFrom).slice(0, 10)
        : dayjs().format('YYYY-MM-DD'),
      validUntil: existing.validUntil
        ? String(existing.validUntil).slice(0, 10)
        : dayjs().add(30, 'day').format('YYYY-MM-DD'),
      authDate: existing.authDate
        ? String(existing.authDate).slice(0, 10)
        : fromApp.requestDate
          ? String(fromApp.requestDate).slice(0, 10)
          : dayjs().format('YYYY-MM-DD'),
      name: existing.name || fromApp.personName || '',
      licenseNo: existing.licenseNo || fromApp.ameLicenseNo || '',
      category: existing.category || fromApp.licenseCategory || '',
      ratings: existing.ratings || fromApp.licenseRatings || '',
      maintenanceOrgName:
        existing.maintenanceOrgName || fromApp.maintenanceOrgNameApproval || '',
      aircraftModel: existing.aircraftModel || fromApp.aircraftType || '',
      aircraftReg: existing.aircraftReg || fromApp.aircraftReg || '',
      aircraftMsn: existing.aircraftMsn || fromApp.msn || '',
      location: existing.location || fromApp.location || '',
      operatorName: existing.operatorName || fromApp.operatorName || '',
      reasonForAuth: existing.reasonForAuth || fromApp.reasonForOneOff || '',
      scopeRows:
        Array.isArray(existing.scopeRows) && existing.scopeRows.length
          ? existing.scopeRows
          : undefined
    });
  };

  const handleIssue = async (issued) => {
    if (!detail?._id) return;
    const cert = detail.certificate || ensureCertificateDraft(detail);
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/${detail._id}/issue`,
        buildCertificatePayload(cert, issued)
      );
      setDetail(res.data);
      showToast(
        issued ? `Authorization issued (${FORM_REFS.certificate})` : 'Marked not issued',
        'success'
      );
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Issue failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCertificatePdf = async () => {
    if (!detail?._id) return;
    try {
      if (canMroQm && detail.certificate) {
        await axios.put(
          `${API}/${detail._id}/certificate`,
          buildCertificatePayload(detail.certificate, detail.certificate.issued)
        );
      }
      const authNo = (detail.certificate?.authorizationNo || 'GETS-ONE-OFF').replace(/\s+/g, '_');
      const name = (detail.personName || detail.certificate?.name || 'authorization').replace(
        /\s+/g,
        '_'
      );
      await downloadAuthenticatedPdf(
        `${API}/${detail._id}/certificate/pdf`,
        `GETS-CM-001A_${authNo}_${name}.pdf`
      );
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to download certificate PDF', 'error');
    }
  };

  const editableStatuses = useMemo(() => ['DRAFT', 'RETURNED_TO_APPLICANT'], []);
  const currentUserId = user?._id || user?.id;
  const isApplicantOwner =
    detail &&
    currentUserId &&
    String(detail.applicantUserId?._id || detail.applicantUserId) === String(currentUserId);
  const canEditDetail =
    detail &&
    editableStatuses.includes(detail.status) &&
    (isApplicantOwner ||
      [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role));

  const canIssueCertificate =
    canMroQm && ['ACCEPTED_BY_MRO_QM', 'ASSESSMENT_COMPLETE'].includes(detail?.status);

  const certificateTabEnabled =
    (canMroQm || detail?.status === 'ISSUED' || detail?.status === 'NOT_ISSUED') &&
    ['ACCEPTED_BY_MRO_QM', 'ASSESSMENT_COMPLETE', 'ISSUED', 'NOT_ISSUED'].includes(
      detail?.status
    );

  const subtitle =
    variant === 'crew'
      ? 'Apply for issue of one-off authorization (GETS/CM/002A).'
      : variant === 'mro'
        ? 'One-off applications; MRO QM review and issue.'
        : `${FORM_REFS.application} → ${FORM_REFS.certificate}`;

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {!embedded && (
        <GetsBrandHeader title="One-Off Authorization" subtitle={subtitle} />
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {embedded ? (
          <Box>
            <Typography variant="h6">One-Off Authorization</Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        ) : (
          <Box />
        )}
        <Stack direction="row" spacing={1}>
          <IconButton onClick={load} title="Refresh">
            <Refresh />
          </IconButton>
          {canApply && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setForm(emptyForm(user));
                setPendingFiles([]);
                setCreateOpen(true);
              }}
            >
              New application
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Person</TableCell>
              <TableCell>Aircraft</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Auth No.</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No applications yet</TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    {row.personName ||
                      `${row.applicantUserId?.firstName || ''} ${row.applicantUserId?.lastName || ''}`.trim() ||
                      '—'}
                  </TableCell>
                  <TableCell>
                    {[row.aircraftType, row.aircraftReg].filter(Boolean).join(' / ') || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[row.status] || row.status}
                      color={STATUS_COLORS[row.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{row.certificate?.authorizationNo || '—'}</TableCell>
                  <TableCell>
                    {row.updatedAt ? dayjs(row.updatedAt).format('YYYY-MM-DD HH:mm') : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openDetail(row._id)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setPendingFiles([]);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>New application — {FORM_REFS.application}</DialogTitle>
        <DialogContent dividers>
          <GetsCm002aApplicationForm
            value={form}
            onChange={setForm}
            pendingFiles={pendingFiles}
            attachmentsSaving={saving}
            onAddAttachmentFiles={handleAddPendingFiles}
            onRemovePendingAttachment={handleRemovePendingFile}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false);
              setPendingFiles([]);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" disabled={saving} onClick={handleCreate}>
            Create draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detail)}
        onClose={() => {
          setDetail(null);
          setDocTab(0);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
          <span>One-Off Authorization — documents</span>
          <IconButton
            onClick={() => {
              setDetail(null);
              setDocTab(0);
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={STATUS_LABELS[detail.status] || detail.status}
                  color={STATUS_COLORS[detail.status] || 'default'}
                />
                <Typography variant="body2">
                  {[detail.aircraftType, detail.aircraftReg].filter(Boolean).join(' · ') || 'Issue'}
                </Typography>
              </Stack>

              <Tabs
                value={docTab}
                onChange={(_, v) => setDocTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label={`1. Application (${FORM_REFS.application})`} />
                <Tab
                  label={`2. Authorization card (${FORM_REFS.certificate})`}
                  disabled={!certificateTabEnabled}
                />
              </Tabs>

              {docTab === 0 && (
                <Stack spacing={2}>
                  <GetsCm002aApplicationForm
                    value={detail}
                    onChange={(next) => setDetail((d) => ({ ...d, ...next }))}
                    readOnly={!canEditDetail}
                    onDownloadPdf={handleDownloadApplicationPdf}
                    attachments={detail.applicationAttachments || []}
                    attachmentsSaving={saving}
                    onAddAttachmentFiles={
                      canEditDetail ? handleUploadApplicationAttachment : undefined
                    }
                    onViewAttachment={viewApplicationAttachment}
                    onDownloadAttachment={downloadApplicationAttachment}
                    onDeleteAttachment={
                      canEditDetail ? handleDeleteApplicationAttachment : undefined
                    }
                  />

                  {canEditDetail && (
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" disabled={saving} onClick={handleSaveDraft}>
                        Save
                      </Button>
                      <Button variant="contained" disabled={saving} onClick={handleSubmit}>
                        Submit to MRO Manager
                      </Button>
                    </Stack>
                  )}

                  {canMroManager && detail.status === 'SUBMITTED' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle1">MRO Manager review</Typography>
                      <TextField
                        select
                        fullWidth
                        label="Decision"
                        value={managerDecision.decision}
                        onChange={(e) =>
                          setManagerDecision((d) => ({ ...d, decision: e.target.value }))
                        }
                      >
                        <MenuItem value="ACCEPTED">Accept (forward to MRO QM)</MenuItem>
                        <MenuItem value="REJECTED">Reject (return to applicant)</MenuItem>
                      </TextField>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remarks"
                        value={managerDecision.remarks}
                        onChange={(e) =>
                          setManagerDecision((d) => ({ ...d, remarks: e.target.value }))
                        }
                      />
                      <Button variant="contained" disabled={saving} onClick={handleManagerDecide}>
                        Submit MRO Manager decision
                      </Button>
                    </>
                  )}

                  {detail.mroManagerCheck?.decision && detail.status !== 'SUBMITTED' && (
                    <Alert
                      severity={
                        detail.mroManagerCheck.decision === 'ACCEPTED' ? 'success' : 'warning'
                      }
                    >
                      MRO Manager: {detail.mroManagerCheck.decision}
                      {detail.mroManagerCheck.remarks
                        ? ` — ${detail.mroManagerCheck.remarks}`
                        : ''}
                    </Alert>
                  )}

                  {canMroQm && detail.status === 'ACCEPTED_BY_MRO_MANAGER' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle1">MRO QM / CMD check</Typography>
                      <TextField
                        select
                        fullWidth
                        label="Decision"
                        value={mroDecision.decision}
                        onChange={(e) => setMroDecision((d) => ({ ...d, decision: e.target.value }))}
                      >
                        <MenuItem value="ACCEPTED">Accept</MenuItem>
                        <MenuItem value="REJECTED">Reject (return to applicant)</MenuItem>
                      </TextField>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Application checked for
                      </Typography>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mroDecision.checkedLicences}
                              onChange={(e) =>
                                setMroDecision((d) => ({
                                  ...d,
                                  checkedLicences: e.target.checked
                                }))
                              }
                            />
                          }
                          label="Licences"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mroDecision.checkedTrainings}
                              onChange={(e) =>
                                setMroDecision((d) => ({
                                  ...d,
                                  checkedTrainings: e.target.checked
                                }))
                              }
                            />
                          }
                          label="Trainings"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mroDecision.checkedExperience}
                              onChange={(e) =>
                                setMroDecision((d) => ({
                                  ...d,
                                  checkedExperience: e.target.checked
                                }))
                              }
                            />
                          }
                          label="Experience"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mroDecision.checkedCompanyApproval}
                              onChange={(e) =>
                                setMroDecision((d) => ({
                                  ...d,
                                  checkedCompanyApproval: e.target.checked
                                }))
                              }
                            />
                          }
                          label="Company Approval"
                        />
                      </FormGroup>
                      <TextField
                        select
                        fullWidth
                        label="Overall result"
                        value={mroDecision.overallResult}
                        onChange={(e) =>
                          setMroDecision((d) => ({ ...d, overallResult: e.target.value }))
                        }
                        sx={{ mt: 1 }}
                      >
                        {RESULT_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remarks"
                        value={mroDecision.remarks}
                        onChange={(e) => setMroDecision((d) => ({ ...d, remarks: e.target.value }))}
                      />
                      <Button variant="contained" disabled={saving} onClick={handleMroDecide}>
                        Submit MRO QM decision
                      </Button>
                    </>
                  )}

                  {detail.mroQmCheck?.decision && detail.status !== 'SUBMITTED' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle2">MRO QM decision</Typography>
                      <Typography variant="body2">
                        {detail.mroQmCheck.decision}
                        {detail.mroQmCheck.overallResult
                          ? ` · ${detail.mroQmCheck.overallResult}`
                          : ''}
                        {detail.mroQmCheck.remarks ? ` — ${detail.mroQmCheck.remarks}` : ''}
                      </Typography>
                    </>
                  )}
                </Stack>
              )}

              {docTab === 1 && (
                <Stack spacing={2}>
                  {detail.status === 'ISSUED' && detail.certificate?.issued && (
                    <Alert severity="success">
                      Issued {detail.certificate.authorizationNo} ·{' '}
                      {detail.certificate.validFrom
                        ? dayjs(detail.certificate.validFrom).format('DD-MM-YYYY')
                        : '?'}{' '}
                      →{' '}
                      {detail.certificate.validUntil
                        ? dayjs(detail.certificate.validUntil).format('DD-MM-YYYY')
                        : '?'}
                    </Alert>
                  )}
                  <GetsCm001aCertificateForm
                    value={ensureCertificateDraft(detail)}
                    onChange={(next) =>
                      setDetail((d) => ({
                        ...d,
                        certificate: { ...(d.certificate || {}), ...next }
                      }))
                    }
                    readOnly={!canIssueCertificate}
                    saving={saving}
                    onIssue={canIssueCertificate ? handleIssue : undefined}
                    onDownloadPdf={handleDownloadCertificatePdf}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default OneOffAuthorizationPage;
